import { IReactionDisposer, reaction } from 'mobx';
import { CommonEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/common-edit-logic';
import { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import { RenderPostionType } from '@src/js/constant';
import type { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import {
    isAudioElement,
    isDomGifPictureModel,
    isMediaElement,
    isVideoElement,
} from '@src/slide/domain/ui-model/elements/element-judge';
import { IMediaManager, MediaRegion } from '@src/slide/application/service/media-manager/media-manager.interface';
import { IRightSideBarManager } from '@src/slide/application/service/right-side-bar-manager/right-side-bar-manager.interface';
import { IAddonHandler } from '@src/slide/application/service/addon/addon-handler.interface';
import DocsConfig from '@tencent/docs-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';

// 关闭插入公式能力时，需同时屏蔽识别公式&编辑公式&双击打开公式
const ENABLE_LATEX = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.insertLatex) ?? true;

export class PictureEditLogic extends CommonEditLogic {
    public selectedListener: IReactionDisposer;
    public showVideoDomListener: IReactionDisposer;
    public videoPosterListener: IReactionDisposer;
    public renderGifPosterListener: IReactionDisposer;
    public mediaManager: IMediaManager | undefined;
    public rightSideBarManager: IRightSideBarManager | undefined;
    declare protected factoryOptions: InterfaceFactoryOptions<IPictureModel>;
    private isVideoSelected = false;
    private isAudioSelected = false;
    private innerMouseDownSelected: boolean;

    public constructor(factoryOptions: InterfaceFactoryOptions<IPictureModel>, component: ComponentFactory) {
        super(factoryOptions, component);
        const { model } = this.factoryOptions;
        this.mediaManager = factoryOptions.mediaManager;
        this.rightSideBarManager = factoryOptions.rightSideBarManager;
        // 事件绑定
        this.component.inner.onMouseDown(this.handleInnerMouseDown);
        this.component.inner.onClick(this.handleInnerClick);
        this.component.inner.onDoubleClick(this.handleInnerDoubleClick);

        if (isMediaElement(model)) {
            this.addSelectedListener();
        }
        if (isVideoElement(model)) {
            this.addShowVideoDomListener();
        }

        this.addRenderGifPosterListener();
    }

    public dispose(): void {
        super.dispose();
        const { model, mediaManager } = this.factoryOptions;
        if (isVideoElement(model)) {
            mediaManager?.deleteVideoInstanceByRegion(model.key, MediaRegion.EDITOR);
        }
        if (isAudioElement(model)) {
            mediaManager?.deleteAudioInstanceByRegion(model.key, MediaRegion.EDITOR);
        }
        this.renderGifPosterListener?.();
        this.showVideoDomListener?.();
    }

    private handleInnerMouseDown = () => {
        const { model } = this.factoryOptions;
        this.innerMouseDownSelected = model.selected;
    };

    private handleInnerClick = () => {
        const { model } = this.factoryOptions;

        if (isVideoElement(model)) {
            if (this.isVideoSelected || (model.selected && this.innerMouseDownSelected)) {
                this.mediaManager?.playVideoByRegion(model.key, MediaRegion.EDITOR);
            }
            this.isVideoSelected = true;
        }

        if (isAudioElement(model)) {
            if (this.isAudioSelected || (model.selected && this.innerMouseDownSelected)) {
                this.mediaManager?.playAudioByRegion(model.key, MediaRegion.EDITOR);
            }
            this.isAudioSelected = true;
        }
    };

    private handleInnerDoubleClick = (e) => {
        // 音视频屏蔽右侧面板图片编辑功能
        if (isMediaElement(this.factoryOptions.model)) {
            return;
        }

        if (this.getUserPriviledgeService().pptEditorState === PPTEditorStateType.EDITABLE) {
            e?.stopPropagation();

            const { model: { props } } = this.factoryOptions;
            // 获取插件类型、插件id
            const { type: addonType, aid: addonAid } = props.addon || {};
            // 获取点击的当前插件快速工具栏的内容
            const manager = this.getAddonManager();
            const addonQuickItems = (manager?.addonHandler as IAddonHandler)
                ?.addonQuickItemsMap?.get(addonAid ?? '') || ['editAddon'];

            if (ENABLE_LATEX && addonType === 'latex' && addonQuickItems && addonQuickItems.indexOf('editAddon') !== -1) {
                // 当编辑公式按钮可用时，双击公式调用编辑公式功能
                this.rightSideBarManager?.setIsPropertyEditorShown(false);
                this.getMenuManager()?.actionReplaceLatex();
            } else {
                // 唤起图片预览
                this.showPicturePreview();
            }

            this.getAppService().viewableServiceLayer.rightSideBarService.hide();
        } else {
            // 唤起图片预览
            this.showPicturePreview();
        }
    };

    private showPicturePreview = () => {
        const { model } = this.factoryOptions;
        this.getImagePreviewService()?.promise()
            .then(service => service.showPreview(model));
    };

    private addSelectedListener = () => {
        const { model } = this.factoryOptions;

        this.selectedListener = reaction(() => model.selected, (selected: boolean) => {
            if (!selected) {
                this.handleModelUnSelected();
            }
        });
    };

    private addShowVideoDomListener = () => {
        const { model, mediaManager } = this.factoryOptions;
        this.showVideoDomListener = reaction(() => mediaManager?.shouldShowMediaElement(
            model.key,
            model.id as string,
            MediaRegion.EDITOR,
        ), () => {
            this.component.updateComponent();
        });
    };

    private handleModelUnSelected = () => {
        const { model } = this.factoryOptions;

        if (isVideoElement(model)) {
            this.mediaManager?.pauseVideoByRegion(model.key, MediaRegion.EDITOR);
        }

        if (isAudioElement(model)) {
            this.mediaManager?.resetAudioByRegion(model.key, MediaRegion.EDITOR);
        }

        this.isVideoSelected = false;
        this.isAudioSelected = false;
    };

    private addRenderGifPosterListener = () => {
        const { model } = this.factoryOptions;
        this.renderGifPosterListener = reaction(() => this.mediaManager?.shouldRenderGifPoster(model.key), () => {
            if (isDomGifPictureModel(model) && this.factoryOptions.region === RenderPostionType.CANVAS) {
                this.component.updateComponent();
            }
        });
    };

    private getAddonManager() {
        return this.getAppService().viewableServiceLayer.addonManager;
    }
}
